/**
 * Trial balance derivation and the tie-out assertion (ARCHITECTURE §3.1) —
 * the wedge. Sums every account's debit and credit columns and reports whether
 * they tie to zero, or exactly by how much they don't.
 */
import type { Account, Currency, JournalEntry, Money } from "./types";
import { add, subtract, zero, isZero } from "./money";
import { buildLedger } from "./ledger";

export interface TrialBalanceRow {
  readonly code: string;
  readonly name: string;
  readonly debit: Money;
  readonly credit: Money;
}

export interface TrialBalance {
  readonly rows: readonly TrialBalanceRow[];
  readonly debitTotal: Money;
  readonly creditTotal: Money;
  /** debitTotal − creditTotal. Zero when it ties. */
  readonly delta: Money;
  readonly tiesOut: boolean;
  readonly scope: string;
}

/**
 * Derive a trial balance for one entity (or the whole group if entityId is
 * omitted). Each account contributes to exactly one column based on the sign
 * of its net movement, mirroring how a printed trial balance reads.
 */
export function trialBalance(
  entries: readonly JournalEntry[],
  accounts: readonly Account[],
  base: Currency,
  scope: string,
  entityId?: string,
): TrialBalance {
  const ledger = buildLedger(entries, accounts, base, entityId);
  const nameByCode = new Map(accounts.map((a) => [a.code, a.name]));

  const rows: TrialBalanceRow[] = [];
  let debitTotal = zero(base);
  let creditTotal = zero(base);

  // Stable ordering by account code.
  const ordered = [...ledger.values()].sort((a, b) => a.code.localeCompare(b.code));

  for (const bal of ordered) {
    const net = subtract(bal.debit, bal.credit); // positive = net debit
    if (isZero(net)) continue;
    const debit = net.amount > 0 ? net : zero(base);
    const credit = net.amount < 0 ? { amount: -net.amount, currency: base } : zero(base);
    rows.push({
      code: bal.code,
      name: nameByCode.get(bal.code) ?? bal.code,
      debit,
      credit,
    });
    debitTotal = add(debitTotal, debit);
    creditTotal = add(creditTotal, credit);
  }

  const delta = subtract(debitTotal, creditTotal);
  return {
    rows,
    debitTotal,
    creditTotal,
    delta,
    tiesOut: isZero(delta),
    scope,
  };
}
